import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { shouldRetryQuery } from './lib/queryRetry'
import { Analytics } from '@vercel/analytics/react'
import { initSession, setAnalyticsUserId } from './lib/analytics'
import { vercelAnalyticsBeforeSend } from './lib/analyticsUrl'
import GA4PageViews from './components/ads/GA4PageViews'
import SectionChangeEvents from './components/ads/SectionChangeEvents'
import MediavineProSync from './components/ads/MediavineProSync'
import AdInsetSync from './components/ads/AdInsetSync'
import { supabase } from './lib/supabase'
import { ToastProvider } from './hooks/useToast'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import MobileNav from './components/layout/MobileNav'
import ScrollToTop from './components/layout/ScrollToTop'
import IosInstallPrompt from './components/shared/IosInstallPrompt'
import { RequireKeith } from './components/auth/RequireKeith'
import ErrorBoundary from './components/shared/ErrorBoundary'
import RouteErrorBoundary from './components/shared/RouteErrorBoundary'
import AuthRedirectNotice from './components/auth/AuthRedirectNotice'
import SetPasswordModal from './components/auth/SetPasswordModal'
import WatchtowerLaunchPopup from './components/watchtower/WatchtowerLaunchPopup'
import SignupEmailPrompt from './components/email/SignupEmailPrompt'
import EnglishOnlyNote from './components/shared/EnglishOnlyNote'
import UpdateReadyPrompt from './components/shared/UpdateReadyPrompt'
import OfflineBar from './components/offline/OfflineBar'
import TransitionConfirmHost from './components/offline/TransitionConfirmHost'
import HomePage from './pages/HomePage'
import {
  BracketPage,
  BuildPage,
  AboutPage,
  AccuracyPage,
  CheckerPage,
  PrivacyPage,
  TermsPage,
  SupportPage,
  PartnersPage,
} from './routePreload'
import { useCommonT } from './i18n'
import { parseCollectionLocation } from './lib/collectionRoute'
import { invalidateDeckDependentCollectionData, registerDeckDependentQueryClient } from './lib/deckDependentCollection'
import { onSavedDecksChangedRemotely } from './lib/savedDecksRemoteSignal'

// Collection cross-device sync plumbing (claim-on-login + cache fallback).
// Lazy so the entry bundle never pulls it in.
const CollectionSync = lazy(() => import('./components/collection/CollectionSync'))
// The deck-box runner and its chip (app-wide, once). Lazy for the same reason.
const DeckRunnerChip = lazy(() => import('./components/collection/DeckRunnerChip'))

// HomePage stays in the entry bundle so the landing-page first paint is
// instant. Every other route is lazy-loaded into its own chunk; the user
// only pays the network cost for the page they actually navigate to.
// This drops the initial JS bundle from ~583 kB single-chunk to ~250 kB
// entry + per-route splits, improving mobile first-paint significantly.
// The nine pages with prerendered HTML are lazy too, but through
// lazyWithPreload (src/routePreload.ts): bootstrap.ts preloads the current
// one before the first render, so the static page is replaced by the real
// page in one commit instead of by a route spinner first.
const CollectionPage = lazy(() => import('./pages/CollectionPage'))
const ScanImportPage = lazy(() => import('./pages/ScanImportPage'))
const RulesPage = lazy(() => import('./pages/RulesPage'))
const ExplorePage = lazy(() => import('./pages/ExplorePage'))
const AttributionKitPage = lazy(() => import('./pages/AttributionKitPage'))
const SharedDeckPage = lazy(() => import('./pages/SharedDeckPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const SharedBinderPage = lazy(() => import('./pages/SharedBinderPage'))
const AccountPage = lazy(() => import('./pages/AccountPage'))
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'))
// The Decks hub. It owns /decks outright now: MyDecksPage, the page that used
// to live there, is gone. Two deck pages with only one linked from the nav is
// what let a whole results ship land where no user could reach it (2026-08-06).
// Its route is UNCONDITIONAL: gating the only deck page behind a flag is what
// created the dead route in the first place.
const DecksPage = lazy(() => import('./pages/DecksPage'))
// Commander Watchtower. Unflagged and safe to be: nothing is reachable until a
// run publishes, and until then every signed-in visitor sees an empty list.
const WatchtowerPage = lazy(() => import('./pages/WatchtowerPage'))
// Where the Android app's native Play paywall returns after a purchase.
const ProActivatedPage = lazy(() => import('./pages/ProActivatedPage'))
// Card search v2: the results page and the per-card page (noindex for now:
// NOINDEX_ROUTES in src/seo/routeMeta.ts, X-Robots-Tag in vercel.json).
const CardSearchPage = lazy(() => import('./pages/CardSearchPage'))
const CardPage = lazy(() => import('./pages/CardPage'))

// /decks-v2/<id> -> /decks/<id>. A bare <Navigate> would drop the deck id and
// dump the user on the list, which is the kind of "redirect works" that is
// worse than a 404 because nothing looks broken.
export function DecksV2Redirect() {
  const { deckId } = useParams()
  return <Navigate to={deckId ? `/decks/${deckId}` : '/decks'} replace />
}

// Every /collection URL renders THIS element, so React keeps one CollectionPage
// instance across them (sibling routes with the same element type reuse it,
// as /decks/:id and /decks/:id/edit keep DecksPage). The page's guards live in
// that instance: the scope remount, the write fence, UnfinishedImportsNotice,
// the import panel and the Retry / Undo toasts. A URL that is not canonical
// (an unknown segment, a bad binder id, binder params off the binder route)
// is replaced with the canonical one, history state kept.
export function CollectionRoute() {
  const location = useLocation()
  const navigate = useNavigate()
  const { normalized } = parseCollectionLocation(location.pathname, location.search)
  // A passive effect, not a layout one: on a first mount the router's own
  // history listener (a layout effect in the router, which runs AFTER its
  // children's) is not subscribed yet, and a replace made before it is lost.
  useEffect(() => {
    if (normalized && normalized !== location.pathname + location.search) {
      navigate(normalized + location.hash, { replace: true, state: location.state })
    }
    // Keyed on the entry: a normalized URL parses clean, so this settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, normalized])
  return <CollectionPage />
}

// Tiny generic spinner for route transitions. Intentionally minimal so it
// flashes briefly without distracting the user. Distinct from LoadingState
// (which is for deck analysis and uses analyzer-specific copy).
function RouteSpinner() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="w-10 h-10 rounded-full border-4 border-gray-800 border-t-purple-500 animate-spin" />
    </div>
  )
}

// Full-page fallback for a route that failed to render or lazy-load. The
// common cause is a deploy replacing the hashed chunks under an open tab;
// chunkReload.ts already auto-reloads once for that, so by the time this
// renders the automatic path has been used up (or the failure is a real
// render error) and the user gets a manual way out instead of a blank page.
function RouteErrorFallback() {
  const t = useCommonT()
  return (
    <div className="flex flex-col items-center justify-center py-24 px-4 text-center space-y-4">
      <p className="text-base font-medium text-gray-200">
        {t('route_error.title')}
      </p>
      <p className="text-sm text-gray-400 max-w-sm">
        {t('route_error.body')}
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-sm font-medium text-white"
      >
        {t('route_error.reload')}
      </button>
    </div>
  )
}

// Boundary around the routed page tree. resetKey (NOT a React key - a key
// here would remount the page tree and wipe its state on every navigation)
// clears a tripped fallback when the user navigates away via Header or
// MobileNav, which stay interactive outside the boundary. While the
// boundary's automatic stale-chunk reload is in flight it shows the same
// spinner as a route transition, so the recovery reads as ordinary loading.
export function RoutesWithRecovery() {
  const location = useLocation()
  return (
    <RouteErrorBoundary
      resetKey={location.pathname}
      fallback={<RouteErrorFallback />}
      reloading={<RouteSpinner />}
    >
      <Suspense fallback={<RouteSpinner />}>
        <AppRoutes />
      </Suspense>
    </RouteErrorBoundary>
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
    },
    mutations: {
      retry: false,
    },
  },
})

// My Decks changed (a save, delete, ownership change, refresh, restore or
// remote reconcile): drop the collection caches that depend on them (the
// spare smart binder rule). The deck-box runner writes saved decks on the
// server, outside useSavedDecks, so its signal counts as a change too.
registerDeckDependentQueryClient(queryClient)
onSavedDecksChangedRemotely(() => invalidateDeckDependentCollectionData())

// The route table, extracted so tests can render PRODUCTION's tree under a
// MemoryRouter instead of mirroring it. App mounts BrowserRouter, which takes
// no initialEntries, and a test that re-declares these routes keeps passing
// after this table regresses - which is exactly the failure that put a whole
// results ship on an unreachable page.
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/bracket" element={<BracketPage />} />
      <Route path="/build" element={<BuildPage />} />
      {/* One element for every collection URL (see CollectionRoute). The
          static scan-import route outranks the catch-all. */}
      <Route path="/collection" element={<CollectionRoute />} />
      <Route path="/collection/binders" element={<CollectionRoute />} />
      <Route path="/collection/binders/:binderId" element={<CollectionRoute />} />
      <Route path="/collection/sealed" element={<CollectionRoute />} />
      {/* Anything else under /collection is normalized by CollectionRoute. */}
      {/* eslint-disable-next-line i18next/no-literal-string -- a route pattern, never shown */}
      <Route path="/collection/*" element={<CollectionRoute />} />
      {/* Where the Android app's scanner hands its cards back. */}
      <Route path="/collection/scan-import" element={<ScanImportPage />} />
      <Route path="/rules" element={<RulesPage />} />
      <Route path="/decks" element={<DecksPage />} />
      <Route path="/decks/:deckId" element={<DecksPage />} />
      {/* The full-screen deck editor, over the deck page (DecksPage keeps
          the detail mounted underneath; see DeckDetailV2's editorOpen). */}
      <Route path="/decks/:deckId/edit" element={<DecksPage />} />
      {/* Anyone who bookmarked the old path while it existed. Vercel
          also 308s these server-side; this covers in-app navigation,
          which never reaches the server. */}
      <Route path="/decks-v2" element={<Navigate to="/decks" replace />} />
      <Route path="/decks-v2/:deckId" element={<DecksV2Redirect />} />
      {/* Retired 2026-09-07: both moved to /explore (spec 2026-09-07-decks-explore-design.md).
          vercel.json also 308s /precons server-side; this covers in-app navigation. */}
      <Route path="/suggest-commander" element={<Navigate to="/explore?tab=suggest" replace />} />
      <Route path="/precons" element={<Navigate to="/explore?tab=precons" replace />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/accuracy" element={<AccuracyPage />} />
      <Route path="/explore" element={<ExplorePage />} />
      <Route path="/checker" element={<CheckerPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/partners" element={<PartnersPage />} />
      <Route path="/partners/attribution-kit" element={<AttributionKitPage />} />
      <Route path="/d/:token" element={<SharedDeckPage />} />
      <Route path="/u/:token" element={<ProfilePage />} />
      {/* A shared binder (binder share, Phase 1): public, no auth wrapper. */}
      <Route path="/b/:token" element={<SharedBinderPage />} />
      <Route path="/account" element={<AccountPage />} />
      <Route path="/pro/activated" element={<ProActivatedPage />} />
      <Route path="/watchtower" element={<WatchtowerPage />} />
      <Route path="/watchtower/checks/:checkId" element={<WatchtowerPage />} />
      <Route path="/watchtower/:reportId" element={<WatchtowerPage />} />
      <Route path="/search" element={<CardSearchPage />} />
      <Route path="/card/:oracleId" element={<CardPage />} />
      <Route
        path="/admin/metrics"
        element={
          <RequireKeith>
            <ErrorBoundary showErrorDetail>
              <AdminDashboardPage />
            </ErrorBoundary>
          </RequireKeith>
        }
      />
      <Route path="*" element={<HomePage />} />
    </Routes>
  )
}

function App() {
  useEffect(() => {
    // Resolve auth state BEFORE initSession() fires session_start so the
    // first event gets queued for ~10-100ms and then drained with the
    // signed-in user_id (or null for anonymous). See analytics.ts queue.
    if (!supabase) {
      setAnalyticsUserId(null)
    } else {
      supabase.auth.getSession().then(({ data }) => {
        setAnalyticsUserId(data.session?.user?.id ?? null)
      })
    }
    initSession()
    if (!supabase) return
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAnalyticsUserId(session?.user?.id ?? null)
    })
    return () => subscription.unsubscribe()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
      <BrowserRouter>
        <ScrollToTop />
        <GA4PageViews />
        <SectionChangeEvents />
        {/* Writes the Pro ad-suppression flag the shell's inline script reads
            at first paint. App-wide like GA4PageViews: the entitlement is not
            about any one route. */}
        <MediavineProSync />
        {/* --ad-inset-bottom for the fixed bottom UI (tab bar, chat pill,
            panel and composer, toasts, overlays) so the adhesion ad never
            covers it, phones included. */}
        <AdInsetSync />
        <div className="flex flex-col min-h-svh">
          <Suspense fallback={null}>
            <CollectionSync />
          </Suspense>
          <Suspense fallback={null}><DeckRunnerChip /></Suspense>
          {/* Both self-arm off the URL a redirect landed on, so they live at
              the root rather than inside any page: the modal that started the
              flow is long gone by the time the user comes back. */}
          <AuthRedirectNotice />
          <SetPasswordModal />
          {/* Claim-gated and silent until a run publishes: signed out it does
              nothing, and signed in it asks the two RPCs once per app load.
              Lives at the root because it is not about any one page. */}
          <WatchtowerLaunchPopup />
          {/* Asks any signed-in account about email, whichever method
              built the account. Renders nothing at all unless
              that account has never answered and never subscribed. */}
          <SignupEmailPrompt />
          <Header />
          {/* Offline (contracts C1, C2, C6): the API is unreachable, the
              account is reconnecting, or "Sign in again". Renders nothing
              online with a usable session. */}
          <OfflineBar />
          {/* C3 step 4 for an account switch: "N changes ... will be removed". */}
          <TransitionConfirmHost />
          <IosInstallPrompt />
          {/* "Update ready" when a new build is waiting (src/lib/serviceWorker.ts).
              Renders nothing until then. */}
          <UpdateReadyPrompt />
          <main className="flex-1">
            {/* The localized "this page is English" notice above the legal
                and partner pages, in every non-English language. Here and not
                in those pages for a chunk reason (see EnglishOnlyNote). */}
            <EnglishOnlyNote />
            <RoutesWithRecovery />
          </main>
          <Footer />
          <MobileNav />
        </div>
      </BrowserRouter>
      </ToastProvider>
      {/* Drops the query on /rules and every /collection path (typed text). */}
      <Analytics beforeSend={vercelAnalyticsBeforeSend} />
    </QueryClientProvider>
  )
}

export default App
